/**
 * Normalized data contract for the harness (agentic chat) component group.
 *
 * Producers vary widely — smartagent callbacks, IDE runtime wire models,
 * OpenCode-style part arrays, raw MCP results — so these interfaces define
 * the ONE shape the components render. Consumers adapt their runtime's
 * events into these types; everything here is plain data (no DOM, no
 * framework types) so adapters can live anywhere.
 */

// ---------------------------------------------------------------- messages

export type THarnessRole = 'user' | 'assistant' | 'system' | 'tool';

export interface IHarnessReasoningPart {
  id: string;
  text: string;
  /** epoch ms; adapters convert wire ISO strings */
  startedAt?: number;
  /** undefined while the model is still thinking */
  endedAt?: number;
}

export interface IHarnessAttachment {
  id: string;
  name: string;
  mediaType: string;
  /** bytes */
  size: number;
  kind: 'image' | 'text' | 'binary';
  /** base64 payload for images and binary previews */
  dataBase64?: string;
  /** inline payload for text attachments */
  text?: string;
  truncated?: boolean;
}

export interface IHarnessUsage {
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
}

export interface IHarnessMessage {
  /** stable id — used as the keyed-repeat key; never reuse across messages */
  id: string;
  role: THarnessRole;
  text: string;
  /** epoch ms */
  createdAt: number;
  updatedAt?: number;
  /** gates the streaming cursor and markdown parse throttling */
  streaming?: boolean;
  /** defaults to role === 'assistant' when undefined */
  markdown?: boolean;
  reasoning?: IHarnessReasoningPart[];
  attachments?: IHarnessAttachment[];
  /** role 'tool' messages render as a tool card */
  toolCall?: IHarnessToolCall;
  usage?: IHarnessUsage;
  error?: string;
  /** consumer passthrough; never interpreted by the components */
  meta?: Record<string, unknown>;
}

// -------------------------------------------------------------- tool calls

export type THarnessToolStatus = 'pending' | 'running' | 'completed' | 'error';

/**
 * MCP-aligned content blocks (mirrors @modelcontextprotocol/sdk
 * CallToolResult.content entries) so tool results keep their structure
 * instead of being string-flattened.
 */
export type THarnessContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; data: string; mimeType: string }
  | { type: 'audio'; data: string; mimeType: string }
  | { type: 'resource'; resource: { uri: string; mimeType?: string; text?: string; blob?: string } }
  | {
      type: 'resource_link';
      uri: string;
      name: string;
      title?: string;
      description?: string;
      mimeType?: string;
      size?: number;
    };

export interface IHarnessMcpAnnotations {
  title?: string;
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
}

export interface IHarnessMcpMeta {
  serverName: string;
  /** un-namespaced tool name (the part after `server__`) */
  toolName: string;
  annotations?: IHarnessMcpAnnotations;
}

/**
 * Tool calls are deliberately generic-plus-kind rather than a closed
 * discriminated union: the set of tools is open (arbitrary MCP servers,
 * app-specific tools), so classification happens at render time through the
 * tool registry. `kind` is an optional explicit override.
 */
export interface IHarnessToolCall {
  id: string;
  /** raw tool name as invoked, including `server__tool` MCP namespacing */
  name: string;
  kind?: string;
  status: THarnessToolStatus;
  input?: unknown;
  output?: unknown;
  /** MCP CallToolResult.content, rendered structurally when present */
  contentBlocks?: THarnessContentBlock[];
  /** MCP CallToolResult.structuredContent */
  structuredContent?: unknown;
  /** MCP CallToolResult.isError */
  isError?: boolean;
  errorText?: string;
  mcp?: IHarnessMcpMeta;
  /** epoch ms */
  startedAt?: number;
  finishedAt?: number;
}

// ------------------------------------------------------------- permissions

export interface IHarnessPermissionRequest {
  id: string;
  sessionId?: string;
  /** epoch ms */
  createdAt?: number;
  title: string;
  /** open set; well-known values today: write | delete | shell | browser */
  type: string;
  metadata?: Record<string, unknown>;
  /** set once answered — the card stays in the transcript showing the outcome */
  response?: THarnessPermissionResponse;
  /** epoch ms */
  respondedAt?: number;
}

export type THarnessPermissionResponse = 'once' | 'always' | 'reject';

export interface IHarnessQuestionOption {
  label: string;
  description?: string;
}

/**
 * A question the agent asks the user mid-run. Renders inline in the
 * transcript like permissions: pending questions pin to the end, answered
 * ones persist showing the chosen answer(s).
 */
export interface IHarnessQuestionRequest {
  id: string;
  sessionId?: string;
  /** epoch ms */
  createdAt?: number;
  question: string;
  options?: IHarnessQuestionOption[];
  multiSelect?: boolean;
  /** allow a free-text answer alongside the options (default true) */
  allowCustom?: boolean;
  /** set once answered — the card stays in the transcript showing the answer */
  response?: string[];
  /** epoch ms */
  respondedAt?: number;
}

export interface IHarnessQuestionResponseDetail {
  requestId: string;
  answers: string[];
  request: IHarnessQuestionRequest;
}

export interface IHarnessPermissionResponseDetail {
  requestId: string;
  response: THarnessPermissionResponse;
  remember: boolean;
  request: IHarnessPermissionRequest;
}

// ------------------------------------------------------- sessions & status

export interface IHarnessSessionMeta {
  id: string;
  title: string;
  createdAt?: number;
  updatedAt?: number;
  messageCount?: number;
  preview?: string;
  /** Optional dees-icon name (e.g. 'lucide:Terminal') shown before the title. */
  icon?: string;
}

/** Detail of `harness-session-context`, fired on right-click of a session item. */
export interface IHarnessSessionContextDetail {
  session: IHarnessSessionMeta;
  /** Viewport coordinates of the pointer, for positioning a context menu. */
  clientX: number;
  clientY: number;
  /** The suppressed contextmenu event, e.g. for DeesContextmenu.openContextMenuWithOptions. */
  originalEvent: MouseEvent;
}

/** A user-defined session group; ordering of `sessionIds` is the display order. */
export interface IHarnessSessionGroup {
  id: string;
  name: string;
  sessionIds: string[];
}

/** Detail of `harness-session-move`: a session was dropped into a group slot. */
export interface IHarnessSessionMoveDetail {
  sessionId: string;
  /** Target group, or null for the ungrouped section. */
  groupId: string | null;
  /** Insertion index within the target section's current display order. */
  index: number;
}

/** Detail of `harness-group-context`, fired on right-click of a group header. */
export interface IHarnessGroupContextDetail {
  group: IHarnessSessionGroup;
  clientX: number;
  clientY: number;
  originalEvent: MouseEvent;
}

export type THarnessTodoStatus = 'pending' | 'in_progress' | 'completed';

export interface IHarnessTodoItem {
  id?: string;
  content: string;
  status: THarnessTodoStatus;
}

export interface IHarnessStatus {
  type: 'idle' | 'busy' | 'error';
  message?: string;
}

// ---------------------------------------------------------------- composer

export interface IHarnessComposerSendDetail {
  text: string;
  attachments: IHarnessAttachment[];
  model?: string;
  reasoningEffort?: string;
}

// --------------------------------------------------------------- streaming

/**
 * Incremental updates for the `applyDelta()` fast path. Deltas mutate the
 * message object already held in `.messages` (so a later full property
 * re-assignment is idempotent) and re-render only the affected message
 * element.
 */
export type THarnessStreamDelta =
  | { type: 'text'; messageId: string; delta: string }
  | { type: 'reasoning-start'; messageId: string; partId: string; startedAt?: number }
  | { type: 'reasoning'; messageId: string; partId: string; delta: string }
  | { type: 'reasoning-end'; messageId: string; partId: string; endedAt?: number }
  | { type: 'tool-update'; messageId: string; tool: Partial<IHarnessToolCall> & { id: string } }
  | { type: 'message-end'; messageId: string; usage?: IHarnessUsage; error?: string };
